import { useCallback, useEffect, useState } from 'react';

export type AsyncResult<T> =
  | { status: 'loading'; data?: undefined; error?: undefined }
  | { status: 'error'; error: unknown; data?: undefined }
  | { status: 'success'; data: T; error?: undefined };

/**
 * Fetches `fn()` whenever `deps` change; exposes {status, data, error} plus
 * a `reload` a screen's "Retry" button can call directly, without a full
 * remount. No caching layer - these are cheap, paginated, tenant-scoped
 * reads (ADR-064 Part P), not a case that needs one.
 */
export function useApi<T>(fn: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> & { reload: () => void; refresh: () => void } {
  const [state, setState] = useState<AsyncResult<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fn().then(
      (data) => {
        if (!cancelled) setState({ status: 'success', data });
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => load(), [load, nonce]);

  /** Re-reads WITHOUT going back to `loading`: the screen keeps what it shows (a scanning session must not be unmounted). */
  const refresh = (): void => {
    fn().then((data) => setState({ status: 'success', data }), () => undefined);
  };

  return { ...state, reload: () => setNonce((n) => n + 1), refresh };
}
